/**
 * @fileoverview Reusable Tooltip Component
 *
 * Provides accessible, customizable tooltips with hover and focus support.
 * Follows existing app patterns from calendar.tsx.
 *
 * @module components/Tooltip
 */

import { useState, type ReactNode } from 'react'
import { HelpCircle } from 'lucide-react'

interface TooltipProps {
  /** Tooltip content text */
  content: string
  /** Optional custom icon (defaults to HelpCircle) */
  icon?: ReactNode
  /** Position of tooltip relative to trigger */
  position?: 'top' | 'bottom' | 'left' | 'right'
  /** Maximum width in Tailwind classes (e.g., 'max-w-xs') */
  maxWidth?: string
  /** Optional CSS classes for trigger */
  triggerClassName?: string
}

export function Tooltip({
  content,
  icon,
  position = 'top',
  maxWidth = 'max-w-xs',
  triggerClassName = '',
}: TooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false)

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2',
  }

  const arrowClasses = {
    top: 'top-full left-1/2 transform -translate-x-1/2 border-t-gray-900',
    bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-900',
    left: 'left-full top-1/2 transform -translate-y-1/2 border-l-gray-900',
    right: 'right-full top-1/2 transform -translate-y-1/2 border-r-gray-900',
  }

  return (
    <div className="relative inline-block">
      {/* Trigger */}
      <button
        type="button"
        className={`inline-flex items-center justify-center text-gray-400 hover:text-cyan-400 transition-colors cursor-help ${triggerClassName}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label="Show tooltip"
      >
        {icon || <HelpCircle className="w-4 h-4" />}
      </button>

      {/* Tooltip content */}
      {showTooltip && (
        <div
          className={`absolute z-50 ${positionClasses[position]} ${maxWidth} bg-gray-900 text-white text-sm rounded-lg p-3 shadow-xl border border-gray-700`}
          role="tooltip"
        >
          <p className="leading-relaxed">{content}</p>

          {/* Arrow */}
          <div
            className={`absolute ${arrowClasses[position]} w-0 h-0 border-4 border-transparent`}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Inline tooltip variant for use within text
 */
interface InlineTooltipProps {
  /** Text to underline and make hoverable */
  text: string
  /** Tooltip content */
  content: string
}

export function InlineTooltip({ text, content }: InlineTooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <span className="relative inline-block">
      <span
        className="border-b border-dashed border-cyan-400 text-cyan-400 cursor-help"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        tabIndex={0}
        role="button"
        aria-label={`Show tooltip for ${text}`}
      >
        {text}
      </span>

      {showTooltip && (
        <span
          className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-sm rounded-lg p-3 shadow-xl border border-gray-700"
          role="tooltip"
        >
          {content}
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  )
}
